import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  FileText,
  FlaskConical,
  GitBranch,
  Mail,
  Maximize2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import {
  DemoApiError,
  DemoCaseDetail,
  DemoImageFinding,
  DemoMessageItem,
  DemoReviewRequest,
  ReviewStatus,
  demoApi,
} from '../api/demo';
import { authApi } from '../api/auth';
import { AppLayout } from '../components/AppLayout';
import { DemoRunStatus } from '../components/DemoRunStatus';
import { DemoAnalysisDiff } from '../components/DemoAnalysisDiff';
import { DemoEvidenceItem, DemoEvidencePanel } from '../components/DemoEvidencePanel';
import { DemoReviewForm, DemoReviewFormValues } from '../components/DemoReviewForm';
import { DemoAttachmentImage, DemoImageLightbox, DemoImagesPanel } from '../components/DemoImagesPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('it-IT');
}

function shortHash(value: string | null | undefined): string {
  if (!value) return '—';
  return value.slice(0, 12);
}

function collectEvidence(
  value: unknown,
  items: DemoEvidenceItem[],
  seen: Set<string>,
): void {
  if (Array.isArray(value)) {
    for (const entry of value) collectEvidence(entry, items, seen);
    return;
  }
  if (value === null || typeof value !== 'object') return;

  const record = value as Record<string, unknown>;
  const messageId = typeof record.message_id === 'string' ? record.message_id : null;
  const logicalPath = typeof record.logical_path === 'string' ? record.logical_path : null;

  if (messageId && logicalPath) {
    const key = `${messageId}|${logicalPath}`;
    if (!seen.has(key)) {
      seen.add(key);
      items.push({
        messageId,
        logicalPath,
        hash: typeof record.content_hash === 'string' ? record.content_hash : null,
        sourceType: typeof record.source_type === 'string' ? record.source_type : null,
      });
    }
  }

  for (const nested of Object.values(record)) {
    collectEvidence(nested, items, seen);
  }
}

function evidenceFor(detail: DemoCaseDetail): DemoEvidenceItem[] {
  const items: DemoEvidenceItem[] = [];
  const seen = new Set<string>();
  collectEvidence(detail.clinical.analysis, items, seen);
  collectEvidence(detail.clinical.expected, items, seen);
  return items;
}

/**
 * Workspace di review di un caso demo (route `/demo/runs/:runId/cases/:resultId`).
 *
 * Tiene separati integrita' tecnica e interpretazione provvisoria, mostra le
 * evidenze per identificativo (mai il body) e consente di correggere o
 * approvare. Un caso fallito non e' approvabile; un conflitto di revisione non
 * sovrascrive quanto l'utente ha scritto.
 */
export function DemoCaseReviewPage() {
  const { runId, resultId } = useParams<{ runId: string; resultId: string }>();
  const token = authApi.getToken();
  const queryClient = useQueryClient();
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<DemoMessageItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<DemoImageFinding | null>(null);

  const {
    data: detail,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['demo-case', resultId],
    queryFn: () => demoApi.getCaseResult(resultId as string),
    enabled: token !== null && Boolean(resultId),
  });

  const { data: messages = [], isLoading: isMessagesLoading } = useQuery({
    queryKey: ['demo-case-messages', resultId],
    queryFn: () => (resultId ? demoApi.getCaseMessages(resultId) : Promise.resolve([])),
    enabled: token !== null && Boolean(resultId),
  });

  const activeMessage = selectedMessage || (messages.length > 0 ? messages[0] : null);
  const imagesBySha = new Map<string, DemoImageFinding>(
    (detail?.images ?? []).map((image) => [image.sha256, image]),
  );

  const mutation = useMutation({
    mutationFn: (payload: DemoReviewRequest) =>
      demoApi.reviewCase(resultId as string, payload),
    onSuccess: () => {
      setConflict(false);
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['demo-case', resultId] });
      queryClient.invalidateQueries({ queryKey: ['demo-run', runId] });
    },
    onError: (mutationError) => {
      if (
        mutationError instanceof DemoApiError &&
        (mutationError.code === 'review_conflict' || mutationError.status === 409)
      ) {
        setConflict(true);
        setSaveError(null);
        return;
      }
      setConflict(false);
      setSaveError(
        mutationError instanceof Error
          ? mutationError.message
          : 'Errore durante il salvataggio',
      );
    },
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const submitReview = (status: ReviewStatus, values: DemoReviewFormValues) => {
    mutation.mutate({
      status,
      correction_data: values.correction_data,
      notes: values.notes,
      expected_revision: detail?.review?.revision ?? 0,
    });
  };

  const handleRefresh = async () => {
    await refetch();
    setConflict(false);
  };

  const hasConsultModule =
    (detail?.images ?? []).some(
      (image) => image.doc_type === 'modulo_consulto',
    ) ||
    (detail?.documents ?? []).some(
      (document) => document.doc_type === 'modulo_consulto',
    );

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Demo casi reali', href: '/demo' },
        { label: detail?.case_key ?? resultId ?? 'Caso' },
      ]}
      logoIcon={<FlaskConical className="size-4 text-primary" />}
      topbarActions={
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            className="gap-1.5 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90"
            onClick={() => {
              if (activeMessage) setSelectedMessage(activeMessage);
              setIsModalOpen(true);
            }}
          >
            <Mail className="size-3.5" />
            Leggi email {messages.length > 0 ? `(${messages.length})` : ''}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="size-3.5" />
            Aggiorna
          </Button>
        </div>
      }
      mainClassName="mail-utility-page"
    >
      <div className="mx-auto max-w-6xl space-y-6">
        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">Caricamento...</div>
        ) : error ? (
          <Card className="border-danger/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-8 text-danger" />
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Errore nel caricamento del caso
                  </h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {(error as Error).message || 'Errore di connessione al server.'}
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                    <RefreshCw className="size-3.5" />
                    Riprova
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : detail ? (
          <>
            {/* Header del caso con pulsante primario per leggere l'email */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-xl font-bold text-foreground">
                    {detail.case_key}
                  </h1>
                  <span data-testid="demo-case-status"><DemoRunStatus status={detail.technical.status} /></span>
                  {hasConsultModule && (
                    <Badge
                      variant="secondary"
                      data-testid="case-module-badge"
                      className="gap-1 font-normal text-xs"
                    >
                      <FileText className="size-3" />
                      Contiene modulo di consulto
                    </Badge>
                  )}
                  {activeMessage?.subject && (
                    <Badge variant="secondary" className="font-normal text-xs">
                      {activeMessage.subject}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {activeMessage?.from_address ? `Da: ${activeMessage.from_address}` : 'Caso clinico'}
                  {activeMessage?.received_at ? ` · Ricevuto: ${formatTimestamp(activeMessage.received_at)}` : ''}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="default"
                  className="gap-2 bg-primary font-semibold text-primary-foreground shadow hover:bg-primary/90"
                  onClick={() => {
                    if (activeMessage) setSelectedMessage(activeMessage);
                    setIsModalOpen(true);
                  }}
                >
                  <Mail className="size-4" />
                  Leggi email originale {messages.length > 0 ? `(${messages.length})` : ''}
                </Button>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="size-3.5" />
                  Aggiorna
                </Button>
              </div>
            </div>

            {/* Email originale del cliente */}
            <Card className="border-primary/20 bg-card overflow-hidden shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/10 bg-primary/5 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Mail className="size-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">
                    Email originale del cliente
                  </h2>
                  {messages.length > 1 && (
                    <div className="flex items-center gap-1 ml-2">
                      {messages.map((m, idx) => (
                        <Button
                          key={idx}
                          size="sm"
                          variant={activeMessage === m ? 'default' : 'outline'}
                          className="h-6 px-2 text-[11px]"
                          onClick={() => setSelectedMessage(m)}
                        >
                          Messaggio {idx + 1}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 text-primary border-primary/30 hover:bg-primary/10"
                  onClick={() => {
                    if (activeMessage) setSelectedMessage(activeMessage);
                    setIsModalOpen(true);
                  }}
                >
                  <Maximize2 className="size-3" />
                  Apri a schermo intero
                </Button>
              </div>
              <CardContent className="p-4 space-y-3">
                {isMessagesLoading ? (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    Caricamento email...
                  </div>
                ) : activeMessage ? (
                  <>
                    <div className="grid gap-2 text-xs md:grid-cols-2 bg-muted/40 p-3 rounded-lg border border-border">
                      <div>
                        <span className="text-muted-foreground">Oggetto: </span>
                        <span className="font-semibold text-foreground">{activeMessage.subject || '—'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Data: </span>
                        <span className="text-foreground">{formatTimestamp(activeMessage.received_at)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Mittente: </span>
                        <span className="font-mono text-foreground">{activeMessage.from_address || '—'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Destinatario: </span>
                        <span className="font-mono text-foreground">{activeMessage.to_addresses?.join(', ') || '—'}</span>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm font-sans leading-relaxed whitespace-pre-wrap select-text ">
                      {activeMessage.body_text ||
                        (activeMessage.body_html ? (
                          <div dangerouslySetInnerHTML={{ __html: activeMessage.body_html }} />
                        ) : (
                          'Nessun testo presente nel corpo del messaggio.'
                        ))}
                    </div>

                    {activeMessage.attachments && activeMessage.attachments.length > 0 && (
                      <div className="rounded-lg border border-border p-2.5 bg-muted/10 text-xs">
                        <span className="font-medium text-muted-foreground">Allegati ({activeMessage.attachments.length}): </span>
                        <span className="text-foreground">
                          {activeMessage.attachments.map((a: any) => a.filename || a.name || 'file').join(', ')}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    Nessun messaggio email trovato per questo caso.
                  </div>
                )}
              </CardContent>
            </Card>

            <DemoImagesPanel
              resultId={resultId as string}
              images={detail.images ?? []}
              documents={detail.documents ?? []}
              onOpen={setSelectedImage}
            />


            <Card>
              <CardContent className="p-0">
                <div className="flex items-center gap-1.5 border-b border-border bg-muted/30 px-4 py-3">
                  <GitBranch className="size-4 text-muted-foreground" />
                  <h2 className="text-sm font-semibold text-foreground">Timeline protetta</h2>
                </div>
                {detail.technical.stages.length > 0 ? (
                  <ol className="space-y-3 p-6">
                    {detail.technical.stages.map((stage, index) => (
                      <li
                        key={`${stage.stage}-${index}`}
                        data-testid={`demo-stage-${stage.stage}`}
                        className="flex items-start gap-3"
                      >
                        <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground">
                              {stage.stage}
                            </span>
                            <DemoRunStatus status={stage.status} />
                          </div>
                          {stage.failure_code && (
                            <div className="font-mono text-xs text-danger">
                              {stage.failure_code}
                            </div>
                          )}
                          {stage.error_summary && (
                            <div className="text-xs text-muted-foreground">
                              {stage.error_summary}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Nessuno stage registrato
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
                  <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <FileText className="size-4 text-muted-foreground" />
                    Interpretazione provvisoria
                  </h2>
                  {detail.clinical.overall_annotation_status && (
                    <Badge
                      variant="outline"
                      data-testid="clinical-annotation-status"
                      className="bg-warning/10 text-warning border-warning/20"
                    >
                      {detail.clinical.overall_annotation_status}
                    </Badge>
                  )}
                </div>
                <div className="space-y-6 p-6">
                  <DemoAnalysisDiff clinical={detail.clinical} />
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Evidenze
                    </h3>
                    <DemoEvidencePanel
                      evidence={evidenceFor(detail)}
                      sourceHash={detail.technical.source_hash}
                      messages={messages}
                      onViewMessage={(msg) => {
                        setSelectedMessage(msg);
                        setIsModalOpen(true);
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
                  <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <ShieldCheck className="size-4 text-muted-foreground" />
                    Integrità tecnica
                  </h2>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {detail.case_key}
                    </Badge>
                    <DemoRunStatus status={detail.technical.status} />
                  </div>
                </div>
                <details className="utility-disclosure" open={detail.technical.status !== 'passed'}><summary>Elaborazioni, allegati e tracciabilità</summary>
                <div className="grid gap-4 p-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Stage corrente</div>
                      <div className="text-sm text-foreground">
                        {detail.technical.current_stage ?? '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Fallimento</div>
                      <div className="font-mono text-sm text-danger">
                        {detail.technical.failure.code ?? '—'}
                      </div>
                      {detail.technical.failure.summary && (
                        <div className="text-xs text-muted-foreground">
                          {detail.technical.failure.summary}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Hash sorgente</div>
                      <div
                        data-testid="demo-source-hash"
                        className="font-mono text-sm text-foreground"
                      >
                        {shortHash(detail.technical.source_hash)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="mb-1 text-xs font-medium text-muted-foreground">
                        Conteggi privacy
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-foreground">
                          <span className="font-semibold">
                            {detail.technical.privacy.findings}
                          </span>{' '}
                          findings
                        </span>
                        <span className="text-foreground">
                          <span className="font-semibold">
                            {detail.technical.privacy.occurrences}
                          </span>{' '}
                          occorrenze
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-medium text-muted-foreground">
                        Inventario allegati
                      </div>
                      <div className="flex gap-4 text-sm text-foreground">
                        <span>
                          <span className="font-semibold">
                            {detail.technical.coverage.attachments}
                          </span>{' '}
                          allegati
                          {detail.technical.coverage.expected_attachments !== null && (
                            <span className="text-muted-foreground">
                              {' '}
                              / {detail.technical.coverage.expected_attachments} attesi
                            </span>
                          )}
                        </span>
                        <span>
                          <span className="font-semibold">
                            {detail.technical.coverage.nested_messages}
                          </span>{' '}
                          annidati
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                </details>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-4 p-6">
                <h2 className="text-sm font-semibold text-foreground">Review</h2>

                {conflict && (
                  <div
                    data-testid="review-conflict"
                    className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-3"
                  >
                    <AlertTriangle className="mt-0.5 size-4 text-warning" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Revisione modificata da un altro revisore
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Aggiorna i dati per vedere la nuova revisione. Il testo che hai
                        scritto è rimasto nel form.
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleRefresh}>
                      <RefreshCw className="size-3.5" />
                      Aggiorna
                    </Button>
                  </div>
                )}

                {saveError && (
                  <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
                    {saveError}
                  </div>
                )}

                <DemoReviewForm
                  detail={detail}
                  canApprove={detail.technical.status === 'passed'}
                  saving={mutation.isPending}
                  onApprove={(values) => submitReview('approved', values)}
                  onCorrect={(values) => submitReview('corrected', values)}
                />
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Nessun dato disponibile per questo caso
            </CardContent>
          </Card>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="flex h-fit max-h-[92dvh] w-[95vw] max-w-[95vw]! sm:max-w-[95vw]! flex-col lg:w-fit!">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Mail className="size-5 text-primary" />
                {activeMessage?.subject || 'Email del consulto'}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Da: <span className="font-medium text-foreground">{activeMessage?.from_address || 'Proprietario / Referente'}</span>
                {activeMessage?.received_at && ` · Data: ${formatTimestamp(activeMessage.received_at)}`}
                {activeMessage?.logical_path && ` · ${activeMessage.logical_path}`}
              </DialogDescription>
            </DialogHeader>

            {messages.length > 1 && (
              <div className="flex flex-wrap gap-2 border-b border-border pb-2">
                {messages.map((m, idx) => (
                  <Button
                    key={idx}
                    size="sm"
                    variant={activeMessage === m ? 'default' : 'outline'}
                    className="h-7 text-xs"
                    onClick={() => setSelectedMessage(m)}
                  >
                    Messaggio {idx + 1}
                    {m.depth > 0 && ` (risposta depth ${m.depth})`}
                  </Button>
                ))}
              </div>
            )}

            <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
              <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-muted/20 p-4 text-sm font-sans leading-relaxed whitespace-pre-wrap select-text lg:w-[72ch] lg:flex-none">
                {activeMessage?.body_text ||
                  (activeMessage?.body_html ? (
                    <div dangerouslySetInnerHTML={{ __html: activeMessage.body_html }} />
                  ) : (
                    'Nessun testo presente nel corpo del messaggio.'
                  ))}
              </div>

              {activeMessage?.attachments && activeMessage.attachments.length > 0 && (
                <div className="max-h-[35dvh] shrink-0 space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/10 p-3 text-xs lg:max-h-none lg:w-[380px]">
                  <div className="font-medium text-muted-foreground">
                    Allegati ({activeMessage.attachments.length}):
                  </div>
                  <ul className="space-y-1 text-muted-foreground">
                    {activeMessage.attachments.map((att: any, idx: number) => {
                      const sha = typeof att.sha256 === 'string' ? att.sha256 : null;
                      const finding = sha ? imagesBySha.get(sha) : undefined;
                      return (
                        <li key={idx} className="flex flex-wrap items-center gap-2">
                          {finding && sha && (
                            <button
                              type="button"
                              aria-label={`Apri immagine ${att.filename || att.name || `allegato ${idx + 1}`}`}
                              onClick={() => {
                                setIsModalOpen(false);
                                setSelectedImage(finding);
                              }}
                              className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted/30"
                            >
                              <DemoAttachmentImage
                                resultId={resultId as string}
                                sha256={sha}
                                page={finding.page}
                                alt={att.filename || att.name || 'Allegato'}
                                className="h-16 w-16 object-cover"
                              />
                            </button>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-foreground">
                              {att.filename || att.name || `Allegato ${idx + 1}`}
                            </span>
                            {att.content_type && ` (${att.content_type})`}
                          </span>
                          {finding && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-5 px-1.5 text-[10px]"
                              onClick={() => {
                                setIsModalOpen(false);
                                setSelectedImage(finding);
                              }}
                            >
                              <Maximize2 className="size-2.5" />
                              Apri immagine
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DemoImageLightbox
          resultId={resultId as string}
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      </div>
    </AppLayout>
  );
}
